<script setup lang="ts">
import { onMounted, ref, shallowReactive } from 'vue';
import { getCellStyle } from '@/functions/utils';
import CulturesComponent from '@/components/CulturesComponent.vue'
import AbsorbanceChart from '@/components/AbsorbanceChart.vue'
import LiveData from '@/components/LiveData.vue';
import { isMainSpecie, isSpecieMi456 } from '@/functions/functions_cultures';

import {
  handleSubmit,
  editPrelevement,
  deletePrelevement,
  resetForm,
  showTable,
  useRecoltePrelevements,
  useRecoltePrelevementsForDisplay,
  updateVolumeTraite,
  updateAbsorbance,
  calculateDeltaForDisplay,
  getAbsorbanceCellStyle,
  getObservationValue
} from '@/functions/functions_prelevements';

import {
  todaycode,
  transformCodeToDate,
  calculateAgeInDays,
  roundFloat,
} from '@/functions/utils';

import { useConfirm } from '@/composables/useConfirm';
import ConfirmModal from '@/components/ConfirmModal.vue';

import { useI18n } from 'vue-i18n';

import { useAuthStore } from '@/stores/useAuthStore'

import Maintenance from '@/components/Maintenance.vue';

const authStore = useAuthStore();

const { t } = useI18n();

const { confirm } = useConfirm();

const allowedObservationTypeIdsDefault = [3, 4, 5, 6, 7, 10];
const allowedObservationTypeIdsMi456 = [1, 2, 3, 4, 5, 6, 7, 8, 9];

const shouldDisplayObservation = (id: number) => {
  if (isSpecieMi456(dataStore.selectedCulture?.specie.id)) return allowedObservationTypeIdsMi456.includes(id);
  return allowedObservationTypeIdsDefault.includes(id);
};

const numericNullableFields = [
  'temperature',
  'absorbance_880nm',
  'vol_recolte',
  'ph_lu',
  'ph_mesure',
  'vol_traite',
  'masse_residuelle',
] as const;

type NumericNullableField = typeof numericNullableFields[number];

function handleEmptyInput(field: NumericNullableField, event: Event) {
  const input = event.target as HTMLInputElement;
  const value = input.value === '' ? null : Number(input.value);
  tempPrelevement[field] = value;
}

function formatPlusSigns(value: number | null | undefined): string {
  if (value == null || value == 0) return ''; // Gère null et undefined
  if (Number.isInteger(value) && value > 0) {
    return '+'.repeat(value);
  }
  return String(value); // Convertit la valeur en chaîne pour les autres cas
}

function updateObservationValue(prelevement: Prelevement, observationTypeId: number, newValue: string) {
  const value = newValue === '' ? null : Number(newValue);
  const obsIndex = prelevement.observation_values.findIndex(o => o.observation_type_id === observationTypeId);

  if (obsIndex !== -1) {
    prelevement.observation_values[obsIndex] = {
      ...prelevement.observation_values[obsIndex],
      value: value
    };
  } else {
    prelevement.observation_values.push({
      observation_type_id: observationTypeId,
      value: value,
      observation_type: dataStore.observationTypes.find(t => t.id === observationTypeId),
    });
  }
}


function isPercentType(id: number) {
  const percentTypes = [1, 7, 10];
  return percentTypes.includes(id);
}

async function handledeletePrelevement(id: number) {
  const accepted = await confirm(t('confirmDelete'));
  if (accepted) {
    await deletePrelevement(id, t);
  }
}

import { useDataStore } from '@/stores/useDataStore';
const dataStore = useDataStore();

const { filteredPrelevements } = useRecoltePrelevements();
const { inversedPrelevements } = useRecoltePrelevementsForDisplay();

const tempPrelevement = shallowReactive<Prelevement>({
  id: 0,
  culture_id: 0,
  daycode: todaycode,
  user: authStore.name,
  commentaires: '',
  absorbance_880nm: null,
  vol_recolte: null,
  ph_lu: null,
  ph_mesure: null,
  vol_traite: 0,
  masse_residuelle: null,
  temperature: null,
  observation_values: [],
});

const showForm = ref(false);

function handleshowForm() {
  showForm.value = true
}

function handleunshowForm() {
  showForm.value = false
}

const formatObservation = (prelevement: Prelevement, typeId: number): string => {
  const value = getObservationValue(prelevement, typeId);
  if (isPercentType(typeId)) {
    return value != null ? `${value.toFixed(2)} %` : '';
  }
  return formatPlusSigns(value);
};

onMounted(async () => {
  if (dataStore.prelevement) {
    dataStore.prelevement.daycode = todaycode;
  }
  dataStore.filtreEspece = null
  dataStore.filtrePBR = null
});
</script>

<template>
  <ConfirmModal />
  <Maintenance />
  <div v-if="authStore.isAuthenticated && authStore.scopes.includes('reader')">
    <div>
      <h1>{{ $t('prelevementTitle') }}</h1>
    </div>

    <div v-if="dataStore.isDataLoading" class="spinner-container module">
      <div class="spinner"></div>
      <p>{{ $t('loadingData') }}</p>
    </div>

    <div v-else class="module">
      <CulturesComponent />
    </div>

    <!-- Tableau des prélèvements -->
    <div v-if="!dataStore.isDataLoading" class="module module-prel">
      <div v-if="showTable && filteredPrelevements.noData">
        <h3>{{ $t('noDataForCulture') }}</h3>
      </div>

      <div v-if="!showTable">
        <h3>{{ $t('pleaseSelectCulture') }}</h3>
      </div>

      <div v-if="dataStore.isPrelevementsLoading" class="spinner-container">
        <div class="spinner"></div>
        <p>{{ $t('loadingSample') }}</p>
      </div>

      <div v-if="!filteredPrelevements.noData && showTable && !dataStore.isPrelevementsLoading">
        <h2>{{ $t('sampleModuleTtitle') }}
          {{ dataStore.selectedCulture?.pbr.numero_pbr }} |
          {{ dataStore.selectedCulture?.specie.specie_code }} |
          {{ dataStore.selectedCulture?.startdaycode.startday_code }}

        </h2>
      </div>

      <div v-if="!filteredPrelevements.noData && showTable && !dataStore.isPrelevementsLoading" class="table-container"
        style="padding-bottom:10px;">
        <table>
          <thead>
            <tr>
              <th colspan="5" class="info-cat">{{ $t('headers.info') }}</th>
              <th :colspan="isSpecieMi456(dataStore.selectedCulture?.specie.id) ? 9 : 6" class="observations-cat">{{
                $t('headers.observations') }}</th>
              <th colspan="1" class="absorbances-cat">{{ $t('headers.absorbances') }}</th>
              <th colspan="3" class="recoltes-cat">{{ $t('headers.recoltes') }}</th>
              <th colspan="3" class="ph-cat">{{ $t('headers.ph') }}</th>
              <th colspan="4" class="masse-seche-cat">{{ $t('headers.masse_seche') }}</th>
              <th colspan="1" class="temperature-cat">{{ $t('headers.temperature') }}</th>
              <th></th>
            </tr>
            <tr>
              <th class="info">{{ $t('columns.code') }}</th>
              <th class="info">{{ $t('columns.date') }}</th>
              <th class="info">{{ $t('columns.age') }}</th>
              <th class="info">{{ $t('columns.user') }}</th>
              <th class="info big">{{ $t('columns.commentaires') }}</th>
              <th v-for="type in dataStore.observationTypes" v-show="shouldDisplayObservation(type.id)"
                class="observations" :key="type.id">
                {{ type.name }}
              </th>

              <th class="absorbances">{{ $t('columns.abs_880') }}</th>
              <th class="recoltes">{{ $t('columns.volume_recolte') }}</th>
              <th class="recoltes">{{ $t('columns.do_par_jour') }}</th>
              <th class="recoltes">{{ $t('columns.theorical_do') }}</th>
              <th class="ph">{{ $t('columns.ph_lu') }}</th>
              <th class="ph">{{ $t('columns.ph_mesure') }}</th>
              <th class="ph">{{ $t('columns.delta') }}</th>
              <th class="masse-seche">{{ $t('columns.volume_traite') }}</th>
              <th class="masse-seche">{{ $t('columns.masse_residuelle') }}</th>
              <th class="masse-seche">{{ $t('columns.ms_mesuree') }}</th>
              <th v-if="isMainSpecie(dataStore.selectedCulture?.specie.id)" class="masse-seche">{{
                $t('columns.ms_calculee') }}</th>
              <th class="temperature">{{ $t('columns.temperature') }}</th>
              <th style="background-color: #b4e6df;"></th>
            </tr>
          </thead>
          <tbody v-if="filteredPrelevements.data.length">
            <tr v-if="!showForm && authStore.scopes.includes('editor')">
              <td><button class="custom-btn" @click="handleshowForm()">&#10133;</button></td>
            </tr>
            <tr v-if="showForm && authStore.scopes.includes('editor')" class="new-prelevement-row">
              <td><input v-model="tempPrelevement.daycode" type="text" :placeholder="todaycode" required /></td>
              <td></td>
              <td></td>
              <td></td>
              <td class="info"><input v-model.lazy="tempPrelevement.commentaires" placeholder="Commentaire" /></td>
              <template v-for="type in dataStore.observationTypes">
                <td v-if="isPercentType(type.id)" v-show="shouldDisplayObservation(type.id)" class="observations"
                  :key="'input-' + type.id">
                  <input type="number" step="1" min="1" max="100" :placeholder="type.name"
                    :value="getObservationValue(tempPrelevement, type.id)"
                    @input="(e) => updateObservationValue(tempPrelevement, type.id, (e.target as HTMLInputElement).value)" />
                </td>
                <td v-else v-show="shouldDisplayObservation(type.id)" class="observations" :key="'select-' + type.id">
                  <select :value="getObservationValue(tempPrelevement, type.id)"
                    @change="(e) => updateObservationValue(tempPrelevement, type.id, (e.target as HTMLSelectElement).value)">
                    <option value="">{{ type.name }}</option>
                    <option v-for="n in 5" :value="n" :key="n">
                      {{ '+'.repeat(n) }}
                    </option>
                  </select>
                </td>
              </template>
              <td class="absorbances"><input v-model="dataStore.calculationInputAbsorbance" type="text"
                  placeholder="(Calcul)"
                  @input="updateAbsorbance(tempPrelevement, dataStore.calculationInputAbsorbance)" />
                <input v-model="tempPrelevement.absorbance_880nm" type="number" step="0.001" />
              </td>
              <td class="recoltes"><input v-model.number="tempPrelevement.vol_recolte" type="number" /></td>
              <td></td>
              <td></td>
              <td class="ph"><input v-model.number="tempPrelevement.ph_lu" type="number" step="0.01" /></td>
              <td class="ph"><input v-model.number="tempPrelevement.ph_mesure" type="number" step="0.01" /></td>
              <td></td>
              <td class="masse-seche"><input v-model="dataStore.calculationInputVolumeTraite" type="text"
                  placeholder="(Calcul)"
                  @input="updateVolumeTraite(tempPrelevement, dataStore.calculationInputVolumeTraite)" />
                <input v-model="tempPrelevement.vol_traite" type="number" step="0.001" />
              </td>
              <td class="masse-seche"><input v-model.number="tempPrelevement.masse_residuelle" type="number"
                  step="0.001" />
              </td>
              <td></td>
              <td></td>
              <td class="temperature">
                <input v-model.number="tempPrelevement.temperature" type="number" step="0.01"
                  @input="handleEmptyInput('temperature', $event)" />
              </td>
              <td><button class="custom-btn" type="button" style="align-items: center;"
                  @click="handleSubmit(tempPrelevement, t), handleunshowForm()">{{
                    dataStore.isEditing ? '✔️' : '&#10133;' }}</button>
                <button v-if="showForm && !dataStore.isEditing" type="button" class="custom-btn"
                  @click="handleunshowForm()">🔙</button>
                <button v-if="dataStore.isEditing" type="button" class="custom-btn"
                  @click="resetForm(tempPrelevement), handleunshowForm()">🔙</button>
              </td>
            </tr>
            <tr v-for="(p, displayIndex) in inversedPrelevements" :key="p.id">
              <td>{{ p.daycode }}</td>
              <td>{{ p.daycode ? transformCodeToDate(p.daycode) : '' }}</td>
              <td>{{ p.daycode ? calculateAgeInDays(p.daycode, dataStore.selectedCulture?.startdaycode.startday_code ||
                '') : '' }}</td>
              <td>{{ p.user }}</td>
              <td>{{ p.commentaires }}</td>
              <td v-for="type in dataStore.observationTypes" v-show="shouldDisplayObservation(type.id)"
                :style="isPercentType(type.id) ? '' : getCellStyle(getObservationValue(p, type.id))" :key="type.id">
                {{ formatObservation(p, type.id) }}
              </td>
              <td :style="getAbsorbanceCellStyle(p.vol_recolte || 0)">
                {{ p.absorbance_880nm ? roundFloat(p.absorbance_880nm) : '' }}
              </td>
              <td>
                {{ p.vol_recolte ? p.vol_recolte.toFixed(0) : '' }}
              </td>
              <td>{{ calculateDeltaForDisplay(displayIndex) }}</td>
              <td>
                {{
                  p.absorbance_880nm !== null && p.vol_recolte && dataStore.selectedCulture
                    ? roundFloat(p.absorbance_880nm * (dataStore.selectedCulture.pbr.volume - p.vol_recolte) /
                      dataStore.selectedCulture.pbr.volume)
                    : null
                }}
              </td>
              <td>{{ p.ph_lu }}</td>
              <td>{{ p.ph_mesure }}</td>
              <td>{{ (p.ph_mesure && p.ph_lu ? (p.ph_mesure - p.ph_lu).toFixed(2) : '') }}</td>
              <td>{{ p.vol_traite ? roundFloat(p.vol_traite) : '' }}</td>
              <td>{{ p.masse_residuelle ? roundFloat(p.masse_residuelle) : '' }}</td>
              <!-- Formule masse mesurée -->
              <td>
                {{ (p.vol_traite && p.masse_residuelle) ? (p.masse_residuelle * 1000 / p.vol_traite).toFixed(3) : '' }}
              </td>
              <!-- Formule masse calculée -->
              <td v-if="isMainSpecie(dataStore.selectedCulture?.specie.id)">
                {{ p.absorbance_880nm !== undefined && p.vol_recolte
                  ? ((p.absorbance_880nm ?? 1) * (dataStore.selectedCulture?.specie.ratio ?
                    dataStore.selectedCulture.specie.ratio :
                    1)).toFixed(3)
                  : '' }}
              </td>
              <td>{{ p.temperature ? roundFloat(p.temperature) : '' }}</td>
              <td>
                <button v-if="authStore.scopes.includes('editor')" class="custom-btn"
                  @click="editPrelevement(p, tempPrelevement), handleshowForm()">✏️</button>
                <button v-if="authStore.scopes.includes('editor')" class="custom-btn"
                  @click="handledeletePrelevement(p.id!)">&#10060;</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

    </div>

    <LiveData v-if="dataStore.selectedCulture" />

    <!-- Section pour l'évolution de l'absorbance -->
    <div v-if="!filteredPrelevements.noData && showTable && !dataStore.isPrelevementsLoading" class="chart"
      style="padding-bottom:10px;">
      <h2>{{ $t('chartTitle') }}</h2>
      <div>
        <AbsorbanceChart :prelevements="filteredPrelevements?.data || []"
          :analyses="dataStore.selectedCulture?.analyses || []" />
      </div>
    </div>

  </div>

  <div v-else class="centered-block">
    <h2>{{ $t('loginRequiredTitle') }}</h2>
    <p>{{ $t('loginRequiredMessage') }}</p>
  </div>
</template>

<style scoped>
.button-group {
  grid-column: 1 / -1;
  /* occupe toutes les colonnes */
  display: flex;
  gap: 10px;
  justify-content: flex-start;
  /* bouton à gauche */
  align-items: center;
  /* vertical centré */
}

/* Les boutons ne s’étirent pas */
.button-group button {
  width: auto;
  height: auto;
  padding: 10px 20px;
  align-self: center;
}

.new-prelevement-row input {
  padding: 5px;
  margin-top: 8px;
}

.new-prelevement-row td {
  padding: 5px;
  border-radius: 10px;
  box-sizing: border-box;
}

.new-prelevement-row th {
  white-space: normal;
  word-break: break-word;
}

.new-prel-dropdown {
  padding: 5px;
  margin-bottom: 10px;
  margin-top: 8px;
  color: #333;
  background-color: #fff;
  border: 2px solid #ccc;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s ease;
}
</style>